import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchMyRequests, fetchReferralOwner, fetchWalletDeals } from '../lib/api.ts';
import { sameAddress, shortAddress, useCollectionNames } from '../lib/collections.ts';
import { GiftArt } from '../components/GiftArt.tsx';
import { useFmt } from '../lib/format.tsx';
import { OfferCard } from './Market.tsx';

/**
 * Витрина приглашающего.
 *
 * Сюда ведёт строка с главной, а не сама ссылка-приглашение: у
 * пригласившего может не быть ни офферов, ни запросов, и встречать
 * человека пустотой — худшее первое впечатление, какое можно устроить.
 * Кто хочет посмотреть, на что его позвали, доходит сюда в одно нажатие.
 *
 * Экран открыт всем и ничего не требует: кошелёк здесь не нужен, приглашение
 * сработает само при первой покупке.
 */
const CARD_BUTTON: React.CSSProperties = {
  gap: 9,
  textAlign: 'left',
  cursor: 'pointer',
  color: 'var(--ink)',
  font: 'inherit',
  alignItems: 'stretch',
};

export function Inviter({
  code,
  onOpen,
  onMarket,
  onBoard,
}: {
  code: string;
  onOpen: (address: string) => void;
  onMarket: () => void;
  onBoard: () => void;
}) {
  const { t } = useTranslation();
  const { money, dateTime } = useFmt();
  const nameOf = useCollectionNames();

  const owner = useQuery({
    queryKey: ['refOwner', code],
    queryFn: () => fetchReferralOwner(code),
    retry: false,
  });
  const address = owner.data?.address ?? '';

  const deals = useQuery({
    queryKey: ['inviterDeals', address],
    queryFn: () => fetchWalletDeals(address),
    enabled: address.length > 0,
  });
  const requests = useQuery({
    queryKey: ['inviterRequests', address],
    queryFn: () => fetchMyRequests(address),
    enabled: address.length > 0,
  });

  // Оффер — это сделка с заложенным гифтом, которая ждёт покупателя, и
  // выставил её именно он. Свои покупки приглашающего тут ни при чём.
  const offers = (deals.data?.deals ?? []).filter(
    (d) => d.state === 1 && address.length > 0 && sameAddress(d.seller, address),
  );
  const open = (requests.data?.requests ?? []).filter(
    (r) => r.status === 'open' || r.status === 'countered',
  );

  if (owner.isError) {
    return (
      <div className="screen">
        <div className="card text">{t('inviter.unknown')}</div>
        <button className="btn" onClick={onMarket}>{t('inviter.to_market')}</button>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="col" style={{ gap: 2 }}>
        <strong style={{ fontSize: 17 }}>{t('inviter.title')}</strong>
        <span className="muted" style={{ fontSize: 13 }}>{t('inviter.lead')}</span>
        {address.length > 0 && (
          <span className="mono dim" style={{ fontSize: 11, marginTop: 2 }}>
            {shortAddress(address, { wallet: true })}
          </span>
        )}
      </div>

      <div className="label">{t('inviter.offers')}</div>
      {(deals.isLoading || owner.isLoading) && <div className="skeleton" style={{ minHeight: 120 }} />}
      {!deals.isLoading && offers.length === 0 && (
        <div className="card muted">{t('inviter.no_offers')}</div>
      )}
      {offers.map((deal) => (
        <OfferCard key={deal.address} deal={deal} onOpen={onOpen} />
      ))}

      <div className="label">{t('inviter.requests')}</div>
      {requests.isLoading && <div className="skeleton" style={{ minHeight: 80 }} />}
      {!requests.isLoading && open.length === 0 && (
        <div className="card muted">{t('inviter.no_requests')}</div>
      )}
      {open.map((r) => {
        const meta = nameOf(r.collection);
        return (
          /* Карточка кликабельна: у того, кто это читает, может оказаться
             такой же гифт — и тогда запрос для него не новость, а работа.
             Ведём на доску, где на него отвечают. */
          <button key={r.id} className="card col" onClick={onBoard} style={CARD_BUTTON}>
            <div className="row" style={{ width: '100%', gap: 11, alignItems: 'flex-start' }}>
              <GiftArt image={meta.image} size={44} radius={11} />
              <div className="col" style={{ flex: 1, minWidth: 0, gap: 3 }}>
                <span style={{ fontSize: 13.5, fontWeight: 700 }}>
                  {meta.name ?? shortAddress(r.collection)}
                </span>
                <span className="mono dim" style={{ fontSize: 9.5 }}>
                  {r.nPayments} × {money((BigInt(r.totalPrice) - BigInt(r.upfront)) / BigInt(r.nPayments))}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="stat-value" style={{ fontSize: 15 }}>{money(r.totalPrice)}</div>
                <div className="label label-xs">{t('inviter.asks')}</div>
              </div>
            </div>
            <div className="row" style={{ width: '100%' }}>
              <span className="dim" style={{ fontSize: 10 }}>{t('market.down')}: {money(r.upfront)}</span>
              <span className="dim" style={{ fontSize: 10 }}>{dateTime(r.expiresAt)}</span>
            </div>
          </button>
        );
      })}

      <button className="btn btn-ghost" onClick={onMarket}>{t('inviter.to_market')}</button>
    </div>
  );
}
